import { Head, Link, WhenVisible, router, useForm, usePage } from "@inertiajs/react";
import { BarChart3, Palette, Timer } from "lucide-react";
import { useRef, useState } from "react";

import { FinalCta } from "@/components/final-cta";
import { GalleryCarousel } from "@/components/gallery-carousel";
import { HeroBeforeAfterGallery } from "@/components/hero-before-after-gallery";
import { PricingTable } from "@/components/pricing-table";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { StickySubscribeForm } from "@/components/sticky-subscribe-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import FaqSection from "@/components/ui/faq-section";
import FeatureSection from "@/components/ui/feature-section";
import { Input } from "@/components/ui/input";
import { useAppearance } from "@/hooks/use-appearance";
import { useFaviconUrl } from "@/hooks/use-favicon-url";
import { useLeadDomain } from "@/hooks/use-lead-domain";
// Helper to track events with Rybbit + Ahoy (client-side only)
const trackEvent = async (name: string, props?: Record<string, unknown>) => {
  if (typeof window === "undefined") return;
  window.rybbit?.event(name, props as Record<string, string> | undefined);
  try {
    const { default: ahoy } = await import("ahoy.js");
    ahoy.track(name, props);
  } catch {
    // no-op
  }
};

const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

export default function Agencies() {
  const { flags } = usePage().props;
  const { data, setData, processing, errors } = useForm<{
    url: string;
    email: string;
    style: string;
  }>({
    url: "",
    email: "",
    style: "default",
  });
  const heroDomainForm = useForm<{ domain: string }>({ domain: "" });

  const { setLeadDomain } = useLeadDomain();
  const { appearance } = useAppearance();
  const urlInputRef = useRef<HTMLInputElement>(null);
  const freeTrialUrlRef = useRef<HTMLInputElement>(null);
  const pricingDomainInputRef = useRef<HTMLInputElement>(null);
  const [isValidDomain, setIsValidDomain] = useState(false);
  const faviconUrl = useFaviconUrl(heroDomainForm.data.domain);

  const sanitizeUrl = (rawValue: string) => {
    let input = rawValue.trim();

    // Remove leading/trailing whitespace and invisible chars
    input = input.replace(/^[\s\u200B-\u200D\uFEFF]+|[\s\u200B-\u200D\uFEFF]+$/g, "");

    // If user pasted a URL without protocol, add https://
    if (input && !/^https?:\/\//i.test(input) && /^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(input)) {
      input = `https://${input}`;
    }

    // Remove accidental spaces in the middle
    input = input.replace(/\s+/g, "");

    return input;
  };

  const stripProtocol = (value: string) => value.replace(/^[a-z]+:\/\//i, "");

  const updateDomain = (rawValue: string) => {
    const sanitized = stripProtocol(sanitizeUrl(rawValue)).split("/")[0];
    heroDomainForm.setData("domain", sanitized);
    heroDomainForm.clearErrors("domain");
    setData("url", sanitized);

    if (DOMAIN_PATTERN.test(sanitized)) {
      setIsValidDomain(true);
    } else {
      setIsValidDomain(false);
      setLeadDomain(null);
    }
  };

  const updateGenerationUrl = (rawValue: string) => {
    const sanitizedUrl = sanitizeUrl(rawValue);
    setData("url", sanitizedUrl);
  };

  const handleGenerationUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateGenerationUrl(event.target.value);
  };

  const handleGenerationUrlPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const pastedText = event.clipboardData.getData("text");
    if (!pastedText) return;

    updateGenerationUrl(pastedText);
  };

  const handleDomainPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const pastedText = event.clipboardData.getData("text");
    if (!pastedText) return;

    updateDomain(pastedText);
  };

  const handleHeroDomainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateDomain(event.target.value);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const cleanedUrl = sanitizeUrl(data.url);
    if (!cleanedUrl) return;

    const trimmedEmail = data.email.trim();

    if (flags.free_one_off_generation) {
      void trackEvent("click-generate-free", {
        url: cleanedUrl,
        style: data.style,
      });

      const payload: { url: string; email?: string; style: string } = {
        url: cleanedUrl,
        style: data.style,
      };
      if (trimmedEmail) {
        payload.email = trimmedEmail;
      }

      router.post(`/make-for-free`, payload, {
        onError: (error) => {
          console.error(error);
        },
      });
    } else {
      void trackEvent("click-checkout", { url: cleanedUrl });

      // Use full page redirect instead of AJAX to allow external redirects
      router.get(`/checkout?url=${encodeURIComponent(cleanedUrl)}`);
    }
  };

  const submitLeadDomain = () => {
    const domain = heroDomainForm.data.domain.trim();
    if (!DOMAIN_PATTERN.test(domain)) {
      heroDomainForm.setError("domain", "Enter a valid domain before continuing.");
      urlInputRef.current?.focus();
      return;
    }

    heroDomainForm.post("/onboarding/add_domain", {
      preserveScroll: true,
      onSuccess: () => {
        setLeadDomain(domain);
        window.location.href = "/sign_up";
      },
      onError: () => {
        urlInputRef.current?.focus();
      },
    });
  };

  const handleHeroDomainSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (heroDomainForm.processing) return;
    submitLeadDomain();
  };

  const handlePricingDomainSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (heroDomainForm.processing) return;
    submitLeadDomain();
  };

  // Determine if dark mode is active
  const isDarkMode =
    appearance === "dark" ||
    (appearance === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  const heroPreviewGallery = {
    before: {
      src: isDarkMode
        ? "/hero-before-vs-after/default-no-image-dark.webp"
        : "/hero-before-vs-after/default-no-image.webp",
      alt: "Generic social preview missing image with gray placeholder",
    },
    after: [
      {
        src: "/hero-before-vs-after/after-1.webp",
        alt: "OG Pilot branded preview with bold headline and call-to-action",
      },
      {
        src: "/hero-before-vs-after/after-2.webp",
        alt: "OG Pilot ecommerce preview highlighting product imagery",
      },
      {
        src: "/hero-before-vs-after/after-3.webp",
        alt: "OG Pilot campaign preview with event details and QR code",
      },
    ],
  };

  const heroHighlights = [
    {
      emoji: "⚡️",
      copy: "Auto-generate on-brand social previews for every page",
    },
    {
      emoji: "🛍️",
      copy: "Tailored ecommerce, event, or podcast pages with auto-pulled images, titles, and key details",
    },
    {
      emoji: "📊",
      copy: "Advanced analytics with link share tracking across major social media platforms",
    },
    {
      emoji: "🖼️",
      copy: "Craft branded templates with your logo and custom styling",
    },
    {
      emoji: "🌍",
      copy: "Unlimited refinements with CDN delivery and optimized sizes",
    },
  ];

  const industryProblems = [
    {
      title: "Design queues stall campaigns",
      description:
        "Teams wait on designers to rebuild images for every page, so launch dates slip and ready buyers never even see the link.",
      icon: Timer,
    },
    {
      title: "No visibility after publishing",
      description:
        "After links go live, marketing loses sight of where previews travel or which channel brings the clicks that drive revenue.",
      icon: BarChart3,
    },
    {
      title: "Generic visuals break trust",
      description:
        "Stock-style thumbnails ignore your colors and copy, so the 3x click lift goes to competitors and trust dips.",
      icon: Palette,
    },
  ];

  const industryUseCases = [
    {
      title: "Nike ecommerce makeover",
      description:
        "See the modeled $117M upside when Nike-level stores swap dull OG previews for on-brand, high-converting share cards.",
      cta: "How the Nike remake works",
      href: "/case-studies/nike-store",
    },
    {
      title: "Brooklyn Botanic Garden launch",
      description:
        "Learn how event imagery with dates, times, and CTAs could fuel a $225K annual lift for cultural experiences.",
      cta: "Tour the BBG scenario",
      href: "/case-studies/brooklyn-botanic-garden",
    },
    {
      title: "Office Ladies podcast boost",
      description:
        "Discover how episode-specific OG art can drive 2K+ extra listens and keep sponsorship CPMs climbing.",
      cta: "See the podcast results",
      href: "/case-studies/office-ladies-podcast",
    },
  ];

  const integrations = [
    {
      name: "Carrd",
      status: "Live guide",
      description:
        "Replace Carrd’s default screenshot with a branded OG Pilot image in minutes — use Publish → Media → Share Image.",
      url: "/carrd-social-image",
    },
    {
      name: "WordPress",
      status: "Live guide",
      description:
        "Install the OG Pilot plugin, connect via OAuth, and auto-generate branded social tags for posts, pages, and products.",
      url: "/wordpress",
    },
    {
      name: "Shopify",
      status: "Coming soon",
      description:
        "Native app is on the way—sync product data automatically and refresh visuals with every inventory update.",
    },
    {
      name: "Webflow",
      status: "Coming soon",
      description: "Connect your CMS collections and launch automation-ready visuals straight from Webflow Designer.",
    },
  ];

  return (
    <>
      <Head title="Stop losing clicks" />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 pb-24 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <PublicHeader />

        {/* Hero Section */}
        <section className="container mx-auto max-w-2xl px-4 pt-12 pb-16 sm:pt-20 sm:pb-24 lg:max-w-6xl">
          <div className="flex flex-col items-start justify-between gap-12 lg:flex-row">
            <div className="w-full max-w-4xl lg:flex-[1.25] xl:flex-[1.4]">
              <div className="mb-3 inline-flex items-center justify-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-1 text-xs font-medium text-orange-600 shadow-sm dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-200">
                Dynamic Open Graph Image Toolkit
              </div>
              <div>
                <h1 className="mb-6 text-5xl font-bold text-balance text-gray-900 md:text-6xl lg:text-7xl lg:whitespace-nowrap dark:text-white">
                  Stop losing
                  <span className="ml-3 underline decoration-orange-500">clicks</span>
                </h1>

                <p className="mx-auto mb-6 max-w-3xl text-left text-base leading-relaxed text-balance text-gray-600 lg:mx-0 lg:text-xl dark:text-gray-300">
                  Replace boring generic social preview images with OG Pilot&apos;s professional branded images that
                  match your website&apos;s design and get 3x more clicks.
                </p>

                <ul className="max-w-lg space-y-4 text-base text-gray-600 lg:text-lg dark:text-gray-300">
                  {heroHighlights.map((highlight) => (
                    <li key={highlight.copy} className="flex gap-3">
                      <span aria-hidden="true">{highlight.emoji}</span>
                      <span>{highlight.copy}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex w-full max-w-xl flex-col items-center gap-8 lg:ml-auto lg:flex-[0.75]">
              <HeroBeforeAfterGallery
                before={heroPreviewGallery.before}
                after={heroPreviewGallery.after}
                className="w-full max-w-md"
              />
            </div>
          </div>

          <div className="mt-20 flex w-full justify-center sm:mt-6">
            <div className="flex flex-col items-center text-center text-xs font-semibold tracking-[0.24em] text-orange-600 uppercase dark:text-orange-300">
              <span className="mb-3">Enter your domain to get started</span>
              <svg
                width="48"
                height="218"
                viewBox="0 0 48 218"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-6 text-orange-500 dark:text-orange-400"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M43.6953 0.553886C43.2025 1.04659 43.1086 1.32814 43.1086 2.64201C43.1086 3.6978 43.2729 4.54243 43.6484 5.41053C44.1412 6.63055 44.1647 6.84171 44.1412 14.8657C44.1178 23.4294 43.9535 28.0514 42.9913 45.601C41.7006 68.8753 40.668 81.0521 38.7671 95.3404C36.8193 110.075 32.6656 130.745 29.1219 143.555C26.6344 152.447 19.9696 172.765 16.9422 180.601C14.1496 187.828 9.92543 196.72 8.35309 198.691L7.88374 199.277L8.02454 196.227C8.30615 190.197 8.23576 173.727 7.93068 173.187C7.60213 172.53 6.49915 171.944 5.65432 171.944C4.66868 171.944 3.54224 173.234 3.23716 174.759C3.09635 175.463 2.86167 179.264 2.74433 183.206C2.36885 195.101 1.89949 200.849 0.749579 207.723C-0.165658 213.19 -0.21259 214.316 0.444503 215.56C1.2424 217.085 1.99337 217.554 3.87078 217.648C5.11457 217.718 5.84206 217.624 7.27359 217.132C9.69075 216.311 15.5811 213.472 22.6683 209.694C32.3839 204.509 31.9381 204.791 31.3983 204.251C30.8116 203.665 19.2655 206.527 11.1692 209.272C8.89285 210.023 6.96851 210.656 6.87464 210.656C6.80423 210.656 6.73382 210.304 6.73382 209.882C6.73382 209.295 7.08585 208.709 8.28269 207.254C14.9475 199.136 19.5706 189.165 26.6344 167.721C33.4165 147.121 35.599 138.98 39.0018 121.266C44.1412 94.6131 46.136 73.0281 47.1451 33.0957C47.3563 24.4148 47.3563 20.3793 47.1216 15.0065C46.6757 4.16704 46.2768 1.11697 45.1738 0.319265C44.4698 -0.149975 44.3524 -0.126513 43.6953 0.553886Z"
                  fill="currentColor"
                />
              </svg>
            </div>
          </div>

          <div className="relative mt-6 flex w-full justify-center">
            <div className="absolute inset-0 z-0 flex items-center justify-center">
              <div
                className="animate-pulse-slow h-full w-full max-w-80 rounded-2xl bg-gradient-to-br from-orange-400 via-orange-300 to-orange-500 opacity-90 blur-sm"
                aria-hidden="true"
              />
            </div>
            <form
              onSubmit={handleHeroDomainSubmit}
              className="relative z-10 flex w-full max-w-80 flex-col rounded-lg border border-orange-200 bg-white/90 p-6 shadow-inner shadow-lg dark:border-orange-500/30 dark:bg-gray-900/80"
            >
              <div className="mb-2 flex items-center rounded-md bg-white/90 px-2 text-gray-900 outline-1 -outline-offset-1 outline-gray-200 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-orange-500 dark:bg-black/60 dark:text-white dark:outline-white/10 dark:focus-within:outline-orange-400">
                <div className="h-7 w-7 shrink-0 rounded text-base text-gray-500 select-none sm:text-sm/6 dark:text-gray-400">
                  {faviconUrl ? (
                    <img src={faviconUrl} alt="Website favicon" className="h-7 w-7 rounded" />
                  ) : (
                    <span className="h-7 w-7 rounded text-xl dark:text-gray-500">🌐</span>
                  )}
                </div>
                <Input
                  ref={urlInputRef}
                  id="domain-input"
                  name="domain"
                  type="text"
                  inputMode="url"
                  placeholder="yourdomain.com"
                  className="flex-1 border-0 text-base shadow-none focus-visible:ring-0"
                  value={heroDomainForm.data.domain}
                  onChange={handleHeroDomainChange}
                  onPaste={handleDomainPaste}
                  disabled={heroDomainForm.processing}
                />
              </div>
              {heroDomainForm.errors.domain ? (
                <p className="mb-2 text-sm text-red-600 dark:text-red-400">{heroDomainForm.errors.domain}</p>
              ) : null}

              <Button
                type="submit"
                disabled={!isValidDomain || heroDomainForm.processing}
                className="mb-1 w-full rounded-md bg-orange-500 px-6 text-base font-semibold text-white shadow-lg transition-colors duration-200 hover:bg-orange-600 focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
              >
                {heroDomainForm.processing ? "Checking domain…" : "Connect domain"}
              </Button>
              <p className="text-sm text-gray-500 dark:text-gray-400">7-day free trial. No credit card required.</p>
            </form>
          </div>
        </section>

        {/* Gallery Showcase Section */}
        <section id="gallery-showcase" className="mx-auto w-full px-4 py-16 sm:px-8 sm:py-24">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
              Real results from real websites
            </h2>
            <p className="mx-auto mb-12 max-w-2xl text-xl text-gray-600 dark:text-gray-300">
              See how OG Pilot transforms social previews for brands worldwide. Every image is AI-generated and
              optimized for maximum engagement.
            </p>
          </div>

          <WhenVisible
            data="gallery"
            buffer={200}
            fallback={
              <div className="flex h-64 items-center justify-center">
                <div className="text-gray-500">Loading gallery...</div>
              </div>
            }
          >
            <GalleryCarousel />
          </WhenVisible>

          <div className="mt-12 text-center">
            <a
              href="/gallery"
              className="inline-flex items-center gap-2 text-lg font-semibold text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
            >
              View all examples
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </section>

        {/* Industry Problem Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-100/70 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950">
          <div className="container mx-auto max-w-6xl px-4 py-16 sm:py-24">
            <div className="mb-12 text-center">
              <p className="mb-3 text-xs font-semibold tracking-[0.3em] text-orange-600 uppercase">
                GENERIC SOCIAL PREVIEWS DRAIN REAL REVENUE
              </p>
              <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
                Weak or missing previews throw away 3x more clicks
              </h2>
              <p className="mx-auto max-w-3xl text-lg text-gray-600 dark:text-gray-300">
                When links ship with dull or missing OG images, potential buyers scroll past. Teams wait on design help,
                guess which shares convert, and post visuals that feel off-brand, so clicks drop, traffic shrinks, and
                real revenue slips away.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {industryProblems.map((problem) => {
                const Icon = problem.icon;
                return (
                  <div
                    key={problem.title}
                    className="group relative overflow-hidden rounded-2xl border border-orange-200/40 bg-white/95 p-8 shadow-lg transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-orange-500/30 dark:bg-gray-900/80"
                  >
                    <div
                      className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-100/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-orange-500/20"
                      aria-hidden="true"
                    />
                    <div className="relative flex h-full flex-col gap-5">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-orange-100 text-xl text-orange-600 dark:bg-orange-500/20 dark:text-orange-200">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{problem.title}</h3>
                      <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                        {problem.description}
                      </p>
                      <div className="mt-auto h-1 w-16 rounded-full bg-orange-200/80 transition-colors duration-300 group-hover:bg-orange-400/60 dark:bg-orange-500/30 dark:group-hover:bg-orange-400/60" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <FeatureSection />

        {/* Pricing Section */}
        <section
          id="pricing"
          className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-100/50 py-16 sm:py-24 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950"
        >
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mb-16 text-center">
              <p className="mb-3 text-xs font-semibold tracking-[0.35em] text-orange-600 uppercase dark:text-orange-300">
                GET STARTED
              </p>
              <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
                Built for marketers that ship scroll-stopping previews
              </h2>
              <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                Bundle every domain, keep unlimited refinements in scope, and share analytics that prove the 3× click
                lift your links promise—all without adding headcount.
              </p>
            </div>

            <PricingTable />

            <div className="mx-auto mt-12 max-w-3xl">
              <Card className="border border-orange-200 bg-white/95 shadow-xl dark:border-orange-500/30 dark:bg-gray-900/85">
                <CardContent className="p-6 sm:p-8">
                  <form onSubmit={handlePricingDomainSubmit} className="space-y-5">
                    <p className="text-center text-xs font-semibold tracking-[0.3em] text-orange-600 uppercase sm:text-left dark:text-orange-300">
                      Ready to get started?
                    </p>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                      <div className="flex flex-1 items-center rounded-md bg-white/90 pr-2 pl-3 text-gray-900 outline-1 -outline-offset-1 outline-gray-200 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-orange-500 dark:bg-black/60 dark:text-white dark:outline-white/10 dark:focus-within:outline-orange-400">
                        <div className="h-7 w-7 shrink-0 rounded text-base text-gray-500 select-none sm:text-sm/6 dark:text-gray-400">
                          {faviconUrl ? (
                            <img src={faviconUrl} alt="Website favicon" className="h-7 w-7 rounded" />
                          ) : (
                            <span className="h-7 w-7 rounded text-xl dark:text-gray-500">🌐</span>
                          )}
                        </div>
                        <Input
                          ref={pricingDomainInputRef}
                          name="pricing-domain"
                          type="text"
                          inputMode="url"
                          placeholder="yourdomain.com"
                          className="flex-1 border-0 bg-transparent text-base shadow-none focus-visible:ring-0"
                          value={heroDomainForm.data.domain}
                          onChange={handleHeroDomainChange}
                          onPaste={handleDomainPaste}
                          disabled={heroDomainForm.processing}
                        />
                      </div>
                      <Button
                        type="submit"
                        className="h-11 w-full bg-orange-500 text-white transition-colors hover:bg-orange-600 focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-6"
                        disabled={!isValidDomain || heroDomainForm.processing}
                      >
                        {heroDomainForm.processing ? "Checking…" : "Connect domain"}
                      </Button>
                    </div>
                    <p className="text-center text-sm text-gray-500 sm:text-left dark:text-gray-400">
                      7-day free trial. No credit card required.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 space-y-3 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Every plan starts with a 7-day free trial, unlimited refinements, and cross-channel analytics.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Need something custom?{" "}
                <a
                  href="mailto:sales@ogpilot.com"
                  className="text-orange-600 hover:text-orange-700 dark:text-orange-400"
                >
                  Talk with our team
                </a>
              </p>
            </div>
          </div>
        </section>

        {/* Agency Case Studies */}
        <section
          id="case-studies"
          className="bg-gradient-to-br from-gray-50 via-white to-orange-50/40 py-16 sm:py-24 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950"
        >
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mb-12 text-center">
              <p className="mb-3 text-xs font-semibold tracking-[0.35em] text-orange-600 uppercase dark:text-orange-300">
                CASE STUDIES
              </p>
              <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
                How agencies can turn OG Pilot into retainer wins
              </h2>
              <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                Explore the playbooks agencies can use to ship better social previews in minutes, prove results with
                analytics, and expand retainers across ecommerce, events, and podcasts.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {industryUseCases.map((useCase) => (
                <Link key={useCase.title} href={useCase.href} className="group block h-full">
                  <Card className="relative flex h-full flex-col overflow-hidden border border-orange-200/50 bg-white/95 shadow-lg transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1 group-hover:shadow-xl dark:border-orange-500/30 dark:bg-gray-900/85">
                    <div
                      className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-100/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-orange-500/20"
                      aria-hidden="true"
                    />
                    <CardContent className="relative flex h-full flex-col space-y-5 p-8">
                      <div>
                        <CardTitle className="text-2xl text-gray-900 dark:text-white">{useCase.title}</CardTitle>
                        <CardDescription className="mt-3 flex-1 text-base leading-relaxed text-gray-600 dark:text-gray-300">
                          {useCase.description}
                        </CardDescription>
                      </div>
                      <span className="relative mt-auto inline-flex items-center gap-2 text-sm font-semibold text-orange-600 transition-colors group-hover:text-orange-700 dark:text-orange-300 dark:group-hover:text-orange-200">
                        {useCase.cta} →
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Integrations */}
        <section className="container mx-auto max-w-6xl px-4 py-16 sm:py-24">
          <div className="mb-12 text-center">
            <p className="mb-3 text-xs font-semibold tracking-[0.3em] text-orange-600 uppercase">INTEGRATIONS</p>
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
              Add OG Pilot to the tools you already trust
            </h2>
            <p className="mx-auto max-w-3xl text-lg text-gray-600 dark:text-gray-300">
              Deep documentation today, native integrations tomorrow. Start with Carrd or WordPress guides and get ready
              for Shopify and Webflow apps built for marketers.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {integrations.map((integration) => {
              const content = (
                <Card
                  key={integration.name}
                  className="h-full border border-orange-200 bg-white/95 shadow-[0_4px_32px_0_rgba(255,153,51,0.09)] transition-shadow hover:shadow-lg dark:border-orange-500/30 dark:bg-gray-900/80"
                >
                  <CardHeader className="flex flex-col gap-2 pt-6 pb-3">
                    <div className="flex items-center justify-between gap-3">
                      <CardTitle className="text-[1.09rem] font-bold text-gray-900 dark:text-white">
                        {integration.name}
                      </CardTitle>
                      <Badge
                        variant="outline"
                        className={`rounded px-2 py-0.5 text-xs font-semibold tracking-tight transition-colors ${
                          integration.status === "Live guide"
                            ? "border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-400 dark:bg-orange-500/10 dark:text-orange-200"
                            : "border-orange-200 bg-amber-50 text-amber-900 dark:border-orange-400 dark:bg-orange-500/20 dark:text-orange-100"
                        }`}
                      >
                        {integration.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 pb-7">
                    <CardDescription className="text-base text-gray-600 dark:text-gray-300">
                      {integration.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );

              return integration.url ? (
                <a
                  key={integration.name}
                  href={integration.url}
                  className="block rounded-xl focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none dark:focus:ring-orange-500"
                >
                  {content}
                </a>
              ) : (
                content
              );
            })}
          </div>
        </section>

        {/* Generation Form */}
        <section className="bg-gray-900 py-16 text-white dark:bg-black">
          <div className="container mx-auto max-w-5xl px-4">
            <div className="mb-10 text-center">
              <p className="mb-3 text-xs font-semibold tracking-[0.3em] text-orange-600 uppercase">NEED JUST ONE?</p>
              <h2 className="text-3xl font-bold md:text-4xl">Generate one-off OG images</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-balance text-white/80">
                Use this form to run a page and we&apos;ll send back 2 branded OG images without asking for a login.
              </p>
            </div>
            <Card className="mx-auto max-w-2xl border border-white/10 bg-white/5 shadow-2xl backdrop-blur">
              <CardContent className="p-8">
                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center rounded-md bg-white/90 pr-2 pl-3 text-gray-900 outline-1 -outline-offset-1 outline-gray-200 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-orange-500 dark:bg-black/60 dark:text-white dark:outline-white/10 dark:focus-within:outline-orange-400">
                      <Input
                        ref={freeTrialUrlRef}
                        name="url"
                        type="text"
                        placeholder="https://yourpage.com"
                        className="w-full rounded-none border-0 bg-transparent text-base shadow-none focus-visible:ring-0 dark:bg-black/60"
                        value={data.url}
                        onChange={handleGenerationUrlChange}
                        onPaste={handleGenerationUrlPaste}
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-1 text-left">
                      <label htmlFor="email" className="text-sm font-medium text-white">
                        Email (optional)
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        value={data.email}
                        onChange={(e) => setData("email", e.target.value)}
                        className="bg-white/90 text-gray-900 focus-visible:ring-orange-400 dark:bg-black/60 dark:text-white"
                      />
                      <p className="text-xs text-white/70">Receive a notification when your gallery is ready.</p>
                    </div>
                    <Button
                      type="submit"
                      disabled={processing}
                      className="flex h-11 min-w-[200px] items-center justify-center rounded-md bg-orange-500 px-5 text-base font-semibold text-white shadow transition-colors duration-200 hover:bg-orange-600 focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {processing ? (
                        "Processing..."
                      ) : (
                        <span className="flex items-center gap-2">
                          {flags.free_one_off_generation ? (
                            "Generate my image"
                          ) : (
                            <>
                              <span>Get started for $2</span>
                              <span className="text-sm line-through opacity-75">$10</span>
                            </>
                          )}
                        </span>
                      )}
                      <span className="ml-2 text-lg">→</span>
                    </Button>
                  </div>

                  {errors.url && <p className="mt-2 text-sm text-red-300">{errors.url}</p>}

                  {errors.email && <p className="mt-2 text-sm text-red-300">{errors.email}</p>}

                  {!flags.free_one_off_generation && (
                    <p className="mt-3 text-center text-xs leading-tight text-white/70">
                      <span className="font-medium text-orange-300">
                        50% off + unlimited refinements for early adopters
                      </span>{" "}
                      • One-time payment • No subscription
                    </p>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>
        </section>

        <FaqSection />

        {/* Support + Final CTA */}
        <FinalCta
          title="Marketers, turn every share into a client win"
          description="Connect OG Pilot to your clients’ domains, auto-generate branded OG images, and prove the click-through lift with built-in analytics. No more designer bottlenecks, no more launch delays—just scroll-stopping previews that close retainers faster."
          highlights={[
            {
              icon: "⚡️",
              text: "Unlimited refinements for campaigns and seasons",
            },
            {
              icon: "📊",
              text: "Client-ready analytics dashboards",
            },
          ]}
          submitLabel="Connect domain"
          subtext="7-day free trial. No credit card required."
        />

        <SiteFooter />
      </div>

      <div className="fixed right-0 bottom-0 z-50 rounded-tl-md border border-orange-300 bg-white/80 p-2.5 text-xs font-semibold text-gray-800 shadow-md backdrop-blur dark:border-orange-800 dark:bg-gray-900/80 dark:text-gray-200">
        by{" "}
        <a
          href="https://x.com/RaulOnRails"
          target="_blank"
          className="text-orange-600 underline-offset-2 hover:text-orange-700 hover:underline dark:text-orange-400 dark:hover:text-orange-300"
          rel="noreferrer"
        >
          @RaulOnRails
        </a>
      </div>

      <StickySubscribeForm />
    </>
  );
}
